import deepEqual from 'fast-deep-equal';
import { ReactNode } from 'react';
import {
    FieldPath, FieldPathValue, FieldValues, UnpackNestedValue, get as getNestedValue
} from 'react-hook-form';

import { LabeledValue, LabeledValueProps } from '..';

import { ArrayDiffValue } from './ArrayDiffValue';
import { DiffValue } from './DiffValue';


type UnpackMaybeArray<TEntity extends FieldValues, TProperty extends FieldPath<TEntity>> =
    NonNullable<UnpackNestedValue<FieldPathValue<TEntity, TProperty>>> extends Array<infer TArrayItem>
        ? TArrayItem
        : UnpackNestedValue<FieldPathValue<TEntity, TProperty>>;

export type DiffLabeledValueProps<
    TEntity extends FieldValues, TProperty extends FieldPath<TEntity>, TIsFieldSet = boolean
> = {
    original: TEntity | undefined;
    updated: TEntity | undefined;
    hideIfUnchanged?: boolean;
    ignoreDelete?: boolean;
} & (TIsFieldSet extends true
  ? {
      fieldSet: TIsFieldSet;
      property: TProperty[];
      renderValue?: (value: TEntity) => ReactNode;
    }
  : {
      fieldSet?: TIsFieldSet;
      property: TProperty;
      renderValue?: (value: UnpackMaybeArray<TEntity, TProperty>) => ReactNode;
  }) & Omit<LabeledValueProps, 'value'>;

export function DiffLabeledValue<TEntity extends FieldValues, TProperty extends FieldPath<TEntity>>({
    original,
    updated,
    property,
    renderValue,
    hideIfUnchanged,
    fieldSet,
    ignoreDelete,
    ...props
}: DiffLabeledValueProps<TEntity, TProperty>) {
    const originalValue = fieldSet
        ? Object.fromEntries(property.map(prop => [ prop, getNestedValue(original, prop) ]))
        : getNestedValue(original, property);
    const updatedValue = fieldSet
        ? Object.fromEntries(property.map(prop => [ prop, getNestedValue(updated, prop) ]))
        : getNestedValue(updated, property);

    const valueChanged = updated && !deepEqual(originalValue, updatedValue);

    const diffProps = {
        isCreate: updated && !original,
        isDelete: ignoreDelete ? (valueChanged && originalValue && !updatedValue) : (original && !updated),
        hideIfUnchanged,
        originalValue,
        updatedValue: ignoreDelete ? (valueChanged ? updatedValue : originalValue) : updatedValue,
        renderValue: renderValue as (value: unknown) => ReactNode
    };

    return (!hideIfUnchanged || valueChanged) ? (
        <LabeledValue
            {...props as LabeledValueProps}
            value={(Array.isArray(originalValue) || Array.isArray(updatedValue)) ? (
                <ArrayDiffValue {...diffProps} />
            ) : (
                <DiffValue {...diffProps} />
            )}
        />
    ) : null;
}
